// src/context/WalletContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import api from "../api/api";
import { withRetry } from "../utils/retry";

const WalletContext = createContext(null);

const POLL_INTERVAL = 60000; // one shared interval, not two separate ones

export function WalletProvider({ children }) {
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletLoading, setWalletLoading] = useState(true);
  const isMounted = useRef(true);

  const refreshWallet = useCallback(async () => {
    try {
      const res = await withRetry(() => api.get("/wallet"));
      if (isMounted.current) {
        setWalletBalance(res.data?.balance || 0);
      }
    } catch (err) {
      // Silent fail on background refresh — keep last known balance on screen
      console.error("Wallet refresh failed:", err?.message);
    } finally {
      if (isMounted.current) setWalletLoading(false);
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    refreshWallet();

    const interval = setInterval(refreshWallet, POLL_INTERVAL);

    // Refresh immediately when the user comes back to the tab/app
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshWallet();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refreshWallet);
    window.addEventListener("wallet-credited", refreshWallet);

    return () => {
      isMounted.current = false;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refreshWallet);
      window.removeEventListener("wallet-credited", refreshWallet);
    };
  }, [refreshWallet]);

  return (
    <WalletContext.Provider
      value={{ walletBalance, walletLoading, refreshWallet }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const ctx = useContext(WalletContext);
  if (!ctx) {
    throw new Error("useWallet must be used inside a WalletProvider");
  }
  return ctx;
}